import React, { MouseEvent, useState } from 'react'
import { Button, colors, DownloadIcon, LockIcon, OverflowTooltip, Tooltip } from '@diversioteam/diversio-ds'
import { generatePath, matchPath, useLocation } from 'react-router-dom'
import { useIsMutating } from '@tanstack/react-query'

import { AppRoute } from 'routing/AppRoute.enum'
import { Mutations } from 'api/mutations.enum'
import { generateCompanyURI } from 'sagas/helpers/generateCompanyURI'
import { usePulseSurveys } from 'hooks/usePulseSurveys'
import { PublishDialog } from 'containers/PulseSurveys/Create/PublishDialog'
import { PulseSurveyLink } from 'components/PulseSurveyLink'
import { useGetPulseSurveyDetail } from 'hooks/pulseSurveys/useGetPulseSurveyDetail'
import { useUpdatePulseSurvey } from 'hooks/pulseSurveys/useUpdatePulseSurvey'
import { ACTION_ALLOWED_STATUTES } from 'containers/PulseSurveys/pulseSurvey.helpers'
import { EditableInput } from 'containers/PulseSurveys/Panel/SurveyListItem/EditableInput'
import surveyIconImg from 'assets/images/pulseSurveys/survey-icon.svg'
import { useDownloadPulseSurveyCSV } from 'hooks/pulseSurveys/useDownloadPulseSurveyCSV'

import * as S from './pulseSurveys.styles'

export const PulseSurveys = () => {
    const [isEditing, setIsEditing] = useState(false)
    const [newTitle, setNewTitle] = useState('')

    const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false)

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const { pathname } = useLocation()

    const { activePulseSurvey } = usePulseSurveys()

    const { data } = useGetPulseSurveyDetail({ surveyUUID: activePulseSurvey?.uuid || '' })

    const mutationUpdate = useUpdatePulseSurvey()

    const activePulseUUID = (activePulseSurvey && activePulseSurvey.uuid) || ''

    const { handleDownloadCSV, isDownloading } = useDownloadPulseSurveyCSV({ surveyUUID: activePulseUUID })

    const isMutating = useIsMutating([Mutations.updatePulseSurvey])

    const matchedPath = matchPath(pathname, {
        path: [generateCompanyURI(AppRoute.PulseSurvey), generateCompanyURI(AppRoute.PulseSurveyTemplates)],
        exact: true,
    })

    if (matchedPath) {
        return null
    }

    if (!activePulseSurvey || !data) {
        return null
    }

    const { display_name: title, uuid } = activePulseSurvey
    const { survey_dump: surveyDump, status } = data

    const handleTogglePublishDialog = () => {
        setIsPublishDialogOpen((prev) => !prev)
    }

    const handleShareClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handlePopoverClose = () => {
        setAnchorEl(null)
    }

    const isPopoverOpen = Boolean(anchorEl)

    const surveyUrl = surveyDump?.collector_url

    const canUpdate = ACTION_ALLOWED_STATUTES.update.includes(status)
    const canPublish = ACTION_ALLOWED_STATUTES.publish.includes(status)
    const canCheckStatus = [...ACTION_ALLOWED_STATUTES.close, 'ready_for_results'].includes(status)
    const canCheckResults = status === 'ready_for_results'
    const hasShareButton = surveyUrl && canCheckStatus && !canCheckResults

    const routes: { label: string; pulseSurveyRoute: AppRoute; disabled?: boolean; tooltip?: string }[] = [
        {
            label: 'Survey',
            pulseSurveyRoute: AppRoute.PulseSurveyCreate,
        },
        {
            label: 'Status',
            pulseSurveyRoute: AppRoute.PulseSurveyStatus,
            disabled: !canCheckStatus,
            tooltip: 'Publish the survey to view its status.',
        },
        {
            label: 'Results',
            disabled: !canCheckResults,
            pulseSurveyRoute: AppRoute.PulseSurveyResults,
            tooltip: 'Results will be available after publishing and collecting responses.',
        },
    ]

    const handleSave = (title: string) => {
        setIsEditing(false)

        if (title !== newTitle) {
            mutationUpdate.mutate({
                surveyUUID: uuid,
                body: {
                    title,
                },
            })
        }

        setNewTitle(title)
    }

    const handleEdit = () => {
        setIsEditing(true)
    }

    return (
        <S.PulseSurveysWrapper>
            <S.Header>
                <img src={surveyIconImg} alt="" />

                {isEditing ? (
                    <EditableInput initialValue={title} onSave={handleSave} />
                ) : (
                    <OverflowTooltip overflowDirection="height">
                        <S.Title onDoubleClick={handleEdit}>{title}</S.Title>
                    </OverflowTooltip>
                )}

                {canUpdate && <S.SavingStatus>{isMutating ? 'Saving' : 'All changes saved'}</S.SavingStatus>}
            </S.Header>

            <S.Routes>
                {routes.map(({ label, pulseSurveyRoute, disabled, tooltip = '' }) =>
                    disabled ? (
                        <Tooltip key={label} title={tooltip} variant="light" size="medium">
                            <S.NavLinkDisabled as="div" key={label}>
                                {label}
                                <LockIcon type="line" width={16} height={16} color={colors.secondary300} />
                            </S.NavLinkDisabled>
                        </Tooltip>
                    ) : (
                        <S.NavLink
                            key={label}
                            to={generateCompanyURI(generatePath(pulseSurveyRoute, { id: uuid }))}
                            activeClassName="active"
                        >
                            {label}
                        </S.NavLink>
                    ),
                )}
            </S.Routes>

            {canPublish && (
                <S.ResponsiveButtonWrapper>
                    <Button color="primary" onClick={handleTogglePublishDialog}>
                        Publish
                    </Button>
                </S.ResponsiveButtonWrapper>
            )}

            {hasShareButton && (
                <>
                    <S.ResponsiveButtonWrapper>
                        <Button color="primary" onClick={handleShareClick}>
                            Share
                        </Button>
                    </S.ResponsiveButtonWrapper>

                    <S.Popover
                        open={isPopoverOpen}
                        anchorEl={anchorEl}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <PulseSurveyLink surveyUrl={surveyUrl} size="small" />
                    </S.Popover>
                </>
            )}

            {canCheckResults && (
                <S.ResponsiveButtonWrapper>
                    <Button
                        onClick={handleDownloadCSV}
                        loading={isDownloading}
                        rightIcon={<DownloadIcon type="line" />}
                        color="primary"
                    >
                        Download CSV
                    </Button>
                </S.ResponsiveButtonWrapper>
            )}

            <PublishDialog open={isPublishDialogOpen} onClose={handleTogglePublishDialog} />
        </S.PulseSurveysWrapper>
    )
}
